import axios from 'axios';
import axiosRetry from 'axios-retry';
import {strings} from "@atttomyx/shared-utils";
import {storage, timezones} from "@atttomyx/react-utils";
import {userUtils} from "../utils";
import {keys, time} from "@atttomyx/shared-constants";
import {API_BASE_URL} from "../constants";

const local = storage.getLocal();

let source = axios.CancelToken.source();

axios.defaults.timeout = time.SECONDS_IN_MINUTE * time.MILLISECONDS_IN_SECOND;
axios.defaults.baseURL = API_BASE_URL;

axiosRetry(axios, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: axiosRetry.isSafeRequestError,
});

export const setupAxiosInterceptors = onUnauthenticated => {
    const onRequestSuccess = config => {
        const token = local.getStr(keys.AUTH_TOKEN);
        const tz = timezones.getTimeZone();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (tz) {
            config.headers["x-tz"] = tz;
        }

        config.cancelToken = source.token;

        return config;
    };

    const onResponseSuccess = response => {
        if (response && response.headers) {
            extractAuthToken(response.headers);
        }
        return response;
    };

    const onResponseError = err => {
        const status = err.status || (err.response ? err.response.status : 0);

        if (status === 401) {
            onUnauthenticated();

        } else if (err.response && err.response.headers) {
            extractAuthToken(err.response.headers);
        }

        return Promise.reject(err);
    };

    axios.interceptors.request.use(onRequestSuccess);
    axios.interceptors.response.use(onResponseSuccess, onResponseError);
};

export const cancelAllRequests = (reason) => {
    source.cancel(reason);
    source = axios.CancelToken.source();
};

export const getLoggedInUser = (success, failure) => {
    axios.get("/api/v1/auth/user")
    .then(response => {
        const json = response.data;

        json.roles = userUtils.normalizeRoles(json.roles);

        success(json);
    })
    .catch(failure);
};

export const findAccounts = (success, failure) => {
    const url = "/api/v1/auth/accounts";

    axios.get(url)
    .then(response => {
        const json = response.data;

        success(json.accounts);
    })
    .catch(failure);
};

export const welcome = (nonce, success, failure) => {
    axios.post("/api/v1/auth/welcome", {
        nonce: nonce,
    })
    .then(response => {
        extractAuthToken(response.headers);
        success();
    })
    .catch(failure);
};

export const login = (email, password, accountId, success, failure) => {
    axios.post("/api/v1/auth/login", {
        email: email,
        password: password,
        accountId: accountId,
    })
    .then(response => {
        extractAuthToken(response.headers);
        success(response.data);
    })
    .catch(failure);
};

export const switchAccounts = (accountId, success, failure) => {
    axios.put("/api/v1/auth/switch", {
        accountId: accountId,
    })
    .then(response => {
        extractAuthToken(response.headers);
        success();
    })
    .catch(failure);
};

export const forgotPassword = (email, phone, method, success, failure) => {
    axios.post("/api/v1/auth/forgot", {
        email: email,
        phone: phone,
        method: method,
    })
    .then(response => {
        success();
    })
    .catch(failure);
};

export const recoverPassword = (email, phone, code, success, failure) => {
    axios.put("/api/v1/auth/recover", {
        email: email,
        phone: phone,
        code: code,
    })
    .then(response => {
        extractAuthToken(response.headers);
        success(response.data);
    })
    .catch(failure);
};

export const changePassword = (existing, password, success, failure) => {
    axios.put("/api/v1/auth/password", {
        existing: existing,
        password: password,
    })
    .then(response => {
        success();
    })
    .catch(failure);
};

export const loggedIn = () => {
    let jwt = local.getStr(keys.AUTH_TOKEN);

    return strings.isNotBlank(jwt);
};

export const storeAuthToken = (jwt) => {
    local.setStr(keys.AUTH_TOKEN, jwt);
};

export const clearAuthToken = () => {
    local.clear(keys.AUTH_TOKEN);
};

const extractAuthToken = (headers) => {
    const bearerToken = headers.authorization;

    if (bearerToken && bearerToken.slice(0, 7) === 'Bearer ') {
        const jwt = bearerToken.slice(7, bearerToken.length);

        storeAuthToken(jwt);
    }
};

const recordLogin = () => {
    const timestamp = Date.now().toString();

    local.setStr(keys.LOGIN_AT, timestamp);
};

export const recordLoginIfNecessary = (failure) => {
    const previous = local.getStr(keys.LOGIN_AT);

    if (previous) {
        const elapsed = Date.now() - previous;
        const seconds = elapsed / time.MILLISECONDS_IN_SECOND;
        const minutes = seconds / time.SECONDS_IN_MINUTE;
        const hours = minutes / time.MINUTES_IN_HOUR;

        console.log(`login ${hours} hours ago`);

        if (!isNaN(hours) && hours >= time.HOURS_IN_DAY) {
            axios.post("/api/v1/auth/recordLogin", {})
                .then(recordLogin)
                .catch(failure);
        }
    } else {
        recordLogin();
    }
};
